import type {DeepPartial} from '@/global';
import type {QRCodeItemObject} from '@PosterWhiteboard/items/qr-code-item.class';
import {QRPayloadField, QRType} from '@Libraries/qr-code/qr-code.types';
import {parseQRUrl} from '@Libraries/qr-code/qr-payload-encoding.library';
import type {UtmFormFieldName, UtmLinkInput, UtmLinkResult, UtmLinkVideoSources, UtmParameter, UtmParameterValues} from './utm-link-builder.types';
import {
  EMPTY_UTM_LINK_INPUT,
  REQUIRED_FIELD_MARKER,
  REQUIRED_UTM_PARAMETERS,
  UTM_PARAMETER_FIELDS,
  UtmFieldCheckResult,
  UtmFormField,
  UtmLinkState,
  UtmVideoDataAttribute,
} from './utm-link-builder.types';

const QUERY_SEPARATOR = '?';
const QUERY_PARAMETER_SEPARATOR = '&';
const QUERY_VALUE_SEPARATOR = '=';

/**
 * The link for what the visitor has typed so far, and whether it is finished enough to copy. The website URL goes through the QR tool's
 * parser, so both free tools agree on what a real web address is and say the same thing when one is not.
 */
export const getUtmLinkResult = (input: UtmLinkInput): UtmLinkResult => {
  const parsedWebsiteUrl = parseQRUrl(input.websiteUrl, QRPayloadField.LINK);
  const validWebsiteUrl = parsedWebsiteUrl.issues.length === 0 ? parsedWebsiteUrl.payload : null;
  const previewLink = getPreviewLink(input, validWebsiteUrl);
  const isComplete = validWebsiteUrl !== null && hasRequiredParameters(input.parameterValues);

  return {previewLink, completeLink: isComplete ? previewLink : null, websiteUrlIssues: parsedWebsiteUrl.issues};
};

export const isUtmLinkInputEmpty = (input: UtmLinkInput): boolean => {
  return (
    input.websiteUrl.trim() === '' &&
    Object.values(input.parameterValues).every((value) => {
      return value.trim() === '';
    })
  );
};

/**
 * What the editor writes onto the chosen design's QR code: the link and nothing else, so the code keeps the colours and shape the
 * designer gave it. The editor applies it as a partial update.
 */
export const getUtmHandoffItemObject = (link: string): DeepPartial<QRCodeItemObject> => {
  return {message: link, qrType: QRType.LINK};
};

/** All three URLs or none: a video without its Safari source, or without its poster, is not worth a half-working player. */
export const readUtmLinkVideoSources = (dataset: DOMStringMap): UtmLinkVideoSources | null => {
  const webmUrl = dataset[UtmVideoDataAttribute.WEBM] ?? '';
  const hevcUrl = dataset[UtmVideoDataAttribute.HEVC] ?? '';
  const posterUrl = dataset[UtmVideoDataAttribute.POSTER] ?? '';
  if (webmUrl === '' || hevcUrl === '' || posterUrl === '') {
    return null;
  }
  return {webmUrl, hevcUrl, posterUrl};
};

/** The link the fields' own placeholders make, shown greyed in the empty link box the way an input shows its placeholder. */
export const getUtmExampleLink = (): string => {
  const parameterValues = {...EMPTY_UTM_LINK_INPUT.parameterValues};
  UTM_PARAMETER_FIELDS.forEach((parameterField) => {
    if (isRequiredUtmParameter(parameterField.parameter)) {
      parameterValues[parameterField.parameter] = window.i18next.t(parameterField.placeholderKey);
    }
  });
  const exampleInput = {websiteUrl: window.i18next.t('pmwjs_utm_builder_website_url_placeholder'), parameterValues};
  return getUtmLinkResult(exampleInput).previewLink ?? exampleInput.websiteUrl;
};

export const isRequiredUtmParameter = (parameter: UtmParameter): boolean => {
  return REQUIRED_UTM_PARAMETERS.includes(parameter);
};

export const getRequiredFieldLabel = (label: string): string => {
  return `${label} ${REQUIRED_FIELD_MARKER}`;
};

/** Checked through `getUtmLinkResult`, so the GA4 result always agrees with the error the form shows. */
export const getUtmFieldCheckResult = (fieldName: UtmFormFieldName, value: string): UtmFieldCheckResult => {
  if (fieldName !== UtmFormField.WEBSITE_URL) {
    return UtmFieldCheckResult.VALID;
  }
  const {websiteUrlIssues} = getUtmLinkResult({...EMPTY_UTM_LINK_INPUT, websiteUrl: value});
  return websiteUrlIssues.length === 0 ? UtmFieldCheckResult.VALID : UtmFieldCheckResult.INVALID;
};

export const getUtmLinkState = (result: UtmLinkResult): UtmLinkState => {
  return result.completeLink === null ? UtmLinkState.INCOMPLETE : UtmLinkState.COMPLETE;
};

/** How many UTM parameters the built link carries: 3 with only the required ones, up to 6. */
export const getFilledUtmParameterCount = (parameterValues: UtmParameterValues): number => {
  return Array.from(getFilledParameters(parameterValues).keys()).length;
};

/** A website URL that is not a real web address yet is shown as typed, with the parameters after it, so the link takes shape as they type. */
const getPreviewLink = (input: UtmLinkInput, validWebsiteUrl: string | null): string | null => {
  if (isUtmLinkInputEmpty(input)) {
    return null;
  }
  if (validWebsiteUrl !== null) {
    return getTaggedLink(validWebsiteUrl, input.parameterValues);
  }

  const typedWebsiteUrl = input.websiteUrl.trim();
  const query = getFilledParameters(input.parameterValues).toString();
  if (query === '') {
    return typedWebsiteUrl;
  }
  const separator = typedWebsiteUrl.includes(QUERY_SEPARATOR) ? QUERY_PARAMETER_SEPARATOR : QUERY_SEPARATOR;
  return `${typedWebsiteUrl}${separator}${query}`;
};

const hasRequiredParameters = (parameterValues: UtmParameterValues): boolean => {
  return REQUIRED_UTM_PARAMETERS.every((parameter) => {
    return parameterValues[parameter].trim() !== '';
  });
};

/** A query or fragment already on the page's address is kept as typed; a UTM parameter it already carries is replaced rather than repeated. */
const getTaggedLink = (websiteUrl: string, parameterValues: UtmParameterValues): string => {
  const taggedUrl = new URL(websiteUrl);
  const filledParameters = getFilledParameters(parameterValues);
  const queryParts = [...getKeptQueryParameters(taggedUrl.search, filledParameters), filledParameters.toString()];
  taggedUrl.search = queryParts
    .filter((queryPart) => {
      return queryPart !== '';
    })
    .join(QUERY_PARAMETER_SEPARATOR);
  return taggedUrl.toString();
};

/** Read as raw text, not through URLSearchParams, which would re-encode the page's own parameters and turn a bare `flag` into `flag=`. */
const getKeptQueryParameters = (search: string, filledParameters: URLSearchParams): string[] => {
  return search
    .slice(QUERY_SEPARATOR.length)
    .split(QUERY_PARAMETER_SEPARATOR)
    .filter((queryParameter) => {
      return !filledParameters.has(queryParameter.split(QUERY_VALUE_SEPARATOR)[0]);
    });
};

const getFilledParameters = (parameterValues: UtmParameterValues): URLSearchParams => {
  const filledParameters = new URLSearchParams();
  UTM_PARAMETER_FIELDS.forEach(({parameter}) => {
    const value = parameterValues[parameter].trim();
    if (value !== '') {
      filledParameters.set(parameter, value);
    }
  });
  return filledParameters;
};
